/* eslint-disable @typescript-eslint/no-floating-promises */

import {clickOnProductOptionSf, clickShippingInfoLinkSf} from '@wix/bi-logger-ec-sf/v2';
import {IControllerConfig} from '@wix/native-components-infra/dist/es/src/types/types';
import {getStylesValues} from '@wix/tpa-settings';
import {createSlotVeloAPIFactory} from '@wix/widget-plugins-ooi/velo';
import {AddToCartActionOption, StoresWidgetID} from '@wix/wixstores-client-core/dist/es/src/constants';
import {generateGuid} from '@wix/wixstores-client-core/dist/es/src/guid-generator';
import {
  actualPrice,
  isPreOrder,
  clientFormattedPriceAfterDiscount,
  clientFormattedPriceBeforeDiscount,
} from '@wix/wixstores-client-core/dist/es/src/productOptions/productUtils';
import {getFormattingFunction} from '@wix/wixstores-client-core';
import {getProductVariantBySelectionIds} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import {HeadingTags} from '@wix/wixstores-client-core/dist/es/src/types/heading-tags';
import {
  BI_PRODUCT_OPTION_ACTION,
  BI_PRODUCT_OPTION_TYPE,
} from '@wix/wixstores-client-storefront-sdk/dist/es/src/constants';
import {AddToCartService} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/AddToCartService/AddToCartService';
import {resolveProductManifestState} from '../common/services/resolveProductManifestState';
import {hasNoVisibleVariants} from '../common/productHiddenVariants';
import {GalleryWidgetViewState} from '@wix/wixstores-client-storefront-sdk/dist/es/src/enums/galleryWidgetViewState';
import {ProductPriceBreakdown} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/ProductPriceBreakdown/ProductPriceBreakdown';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {ProductOptionType} from '@wix/wixstores-graphql-schema';
import type {ControllerParams} from '@wix/yoshi-flow-editor';
import {DangerousPublicDataOverrideFn} from '@wix/yoshi-flow-editor-runtime/build/cjs/helpers/createDangerousPublicDataOverride';
import _ from 'lodash';
import {
  AddToCartActionStatus,
  AddToCartFlowType,
  BI_APP_NAME,
  BiEventParam,
  Experiments,
  origin,
  ProductItemSlotIds,
  STORES_NAMESPACE,
} from '../constants';
import {ProductItemsService} from '../services/ProductItemsService';
import {ProductsVariantInfoMap} from '../services/ProductsOptionsService';
import {ProductsService} from '../services/ProductsService';
import {WishlistService} from '../services/WishlistService';
import {IStylesParamsValues} from '../styleParams/types';
import {
  AddToCartButtonPlacement,
  ButtonContentType,
  IGalleryControllerConfig,
  IProductGroup,
  IPropsInjectedByViewerScript as IGalleryPropsInjectedByViewerScript,
  ImageModeId,
  ImageRatioId,
  IProduct,
  LoadMoreType,
  NamePriceLayout,
  PriceAndDiscountLayouts,
  ProductItemsState,
  ProductsManifest,
  type VeloInputs,
} from '../types/galleryTypes';
import {IPropsInjectedByViewerScript as ISliderGalleryPropsInjectedByViewerScript} from '../types/sliderGalleryTypes';
import {ReportError} from './GalleryController';
import {roundStyleParams} from './utils';
import {ReadOnlyExperiments} from '@wix/wix-experiments';
import {ViewerContext} from '@wix/wixstores-client-storefront-common';

export abstract class BaseGalleryStore {
  protected config: IGalleryControllerConfig;
  protected readonly publicData: IControllerConfig['publicData'];
  protected readonly impressionId = generateGuid();
  protected addedToCartStatus: {[p: string]: AddToCartActionStatus} = {};
  protected productPriceBreakdown: ProductPriceBreakdown;
  protected productsManifest: ProductsManifest = {};
  protected productGroupsMap: {[productId: string]: IProductGroup} = {};
  protected productItemsService: ProductItemsService;
  protected productsService: ProductsService;
  protected addToCartService: AddToCartService;
  protected wishlistService: WishlistService;
  protected styles: IStylesParamsValues;
  protected allStyles: IStylesParamsValues[];
  protected appSettings: Record<string, any> = {};
  protected isCatalogV3: boolean;
  protected abstract get stylesParams();
  public readonly experiments: ReadOnlyExperiments;
  protected viewState = GalleryWidgetViewState.DEFAULT;

  constructor(
    config: IControllerConfig,
    protected readonly dangerousPublicDataOverride: DangerousPublicDataOverrideFn,
    protected readonly siteStore: SiteStore,
    protected readonly viewerContext: ViewerContext,
    protected readonly updateComponent: (
      props: Partial<IGalleryPropsInjectedByViewerScript> | Partial<ISliderGalleryPropsInjectedByViewerScript>
    ) => void,
    protected readonly type: string,
    protected readonly compId: string,
    protected readonly flowAPI: ControllerParams['flowAPI'],
    protected readonly reportError: ReportError,
    protected readonly slotAPIFactory: ReturnType<typeof createSlotVeloAPIFactory>,
    protected veloInputs?: VeloInputs
  ) {
    this.config = config;
    this.publicData = _.cloneDeep(this.config.publicData);
    this.updateStyles();

    if (this.flowAPI.experiments.enabled(Experiments.UseExperimentsFromPlatformFlowApi)) {
      this.experiments = this.flowAPI.experiments;
    } else {
      this.experiments = this.siteStore.experiments;
    }

    //todo: COMPONENT === null is not tested, be this check can be removed after bolt will stop sending nulls https://wix.slack.com/archives/CAKBA7TDH/p1555852184003900
    /* istanbul ignore next: hard to test */
    if (this.config.publicData.COMPONENT === null || this.config.publicData.COMPONENT === undefined) {
      this.config.publicData.COMPONENT = {};
    }
    this.productItemsService = new ProductItemsService(this.updateComponent);
    this.wishlistService = new WishlistService(this.siteStore, this.reportError);
  }

  protected updateStyles() {
    const {
      style: {styleParams},
      allStyles,
    } = this.config;
    roundStyleParams(styleParams);
    this.styles = getStylesValues(styleParams, this.stylesParams);
    this.allStyles = allStyles.map((styles) => getStylesValues(styles, this.stylesParams));
  }

  protected isTrueInAnyBreakpoint(predicate: (bpStyle: IStylesParamsValues) => boolean) {
    return this.allStyles.some((bpStyle) => predicate(bpStyle));
  }

  protected setProductWishlistState(productId: string, shouldAdd: boolean): void {
    this.wishlistService.setProductWishlistState(productId, shouldAdd);
    this.updateComponent({userWishlistMap: this.wishlistService.getUserWishlistMap()});
  }

  public async handleWishlistToggle(productId: string, shouldAdd: boolean): Promise<void> {
    if (!this.siteStore.usersApi.currentUser.loggedIn) {
      await this.siteStore.usersApi.promptLogin({});
    }

    const wishlistActionPromise = shouldAdd
      ? this.wishlistService.addProduct(productId)
      : this.wishlistService.removeProduct(productId);

    this.setProductWishlistState(productId, shouldAdd);
    this.reportWishlistActionStart(productId, shouldAdd);

    await wishlistActionPromise
      .then(() => {
        this.reportWishlistActionSuccess(productId, shouldAdd);
        this.productsService.sendWishlistTrackEvent(productId, shouldAdd);
      })
      .catch(() => {
        this.setProductWishlistState(productId, !shouldAdd);
      });
  }

  protected getCommonPropsToInject() {
    this.computeAllClientFormattedPrices();
    return {
      htmlTags: this.htmlTags,
      priceBreakdown: this.priceBreakdown,
      sendClickShippingInfoLinkSf: this.sendClickShippingInfoLinkSf.bind(this),
      imageMode: this.imageMode,
      imageRatio: this.imageRatio,
      isSEO: this.siteStore.seo.isInSEO(),
      updateAddToCartStatus: this.updateAddToCartStatus.bind(this),
      handleQuickAddToCart: this.handleQuickAddToCart.bind(this),
      closeQuickAdd: (productId: string) => this.productsService.closeQuickAdd(this.productsManifest, productId),
      handleWishlistToggle: this.handleWishlistToggle.bind(this),
      isSliderGallery: this.isSliderGallery,
      storesWidgetId: this.storesWidgetId,
      userWishlistMap: this.wishlistService.getUserWishlistMap(),
      clientFormattedPrices: this.clientFormattedPrices,
    };
  }

  private clientFormattedPrices: Record<
    string,
    {formattedPricePerUnit?: string; primaryPrice?: string; secondaryPrice?: string; fromPrice?: string}
  > = {};

  private computeAllClientFormattedPrices() {
    if (!this.experiments.enabled(Experiments.OnlineStoresCurrencyClientFormatting)) {
      return;
    }
    const products = this.productsService.products ?? /* istanbul ignore next */ [];
    const variantInfoMap = this.productsVariantInfoMap;
    products.forEach((product) => {
      const variant = variantInfoMap[product.id]
        ? getProductVariantBySelectionIds({
            product,
            variantSelectionIds: variantInfoMap[product.id].variantSelectionIds,
          })
        : undefined;
      this.recomputeClientFormattedPrices(product, variant);
    });
  }

  private recomputeClientFormattedPrices(product: any, variant?: any) {
    if (!this.experiments.enabled(Experiments.OnlineStoresCurrencyClientFormatting)) {
      return;
    }
    const formatCurrency = getFormattingFunction({flowAPI: this.flowAPI, formatingCurrency: product.currency});
    const variantPricePerUnit = variant?.pricePerUnit ?? product.pricePerUnit;
    const fromPriceNumeric = product.itemDiscount?.priceAfterDiscountAmount ?? product.priceRange?.fromPrice;
    const effectiveVariant = variant
      ? {...variant, price: variant.price ?? product.price, comparePrice: variant.comparePrice ?? product.comparePrice}
      : undefined;
    const effectiveProduct = effectiveVariant
      ? {...product, price: effectiveVariant.price, comparePrice: effectiveVariant.comparePrice}
      : product;
    const includeVariantDiscount = this.experiments.enabled(Experiments.AddDiscountsToVariantsItemsQueries);
    this.clientFormattedPrices[product.id] = {
      formattedPricePerUnit: variantPricePerUnit ? formatCurrency(variantPricePerUnit) : undefined,
      primaryPrice: clientFormattedPriceAfterDiscount({
        product: effectiveProduct,
        flowAPI: this.flowAPI,
        variant: effectiveVariant,
        includeVariantDiscount,
      }),
      secondaryPrice: clientFormattedPriceBeforeDiscount({
        product: effectiveProduct,
        flowAPI: this.flowAPI,
        variant: effectiveVariant,
      }),
      fromPrice: fromPriceNumeric ? formatCurrency(fromPriceNumeric) : undefined,
    };
  }

  protected abstract reportProductItemClick({productId, index}: {productId: string; index: number});

  protected abstract reportWishlistActionStart(productId: string, shouldAdd: boolean): void;

  protected abstract reportWishlistActionSuccess(productId: string, shouldAdd: boolean): void;

  protected sendClickShippingInfoLinkSf(productId: string) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.siteStore.webBiLogger.report(clickShippingInfoLinkSf({productId}));
  }

  protected getCommonPropsToUpdate() {
    const htmlTags = this.htmlTags;
    const productsVariantInfoMap = this.productsVariantInfoMap;
    const imageMode = this.imageMode;
    const imageRatio = this.imageRatio;
    return {htmlTags, productsVariantInfoMap, imageMode, imageRatio};
  }

  protected get priceBreakdown() {
    return {
      shouldRenderTaxDisclaimer: this.productPriceBreakdown.shouldShowTaxDisclaimer,
      shippingDisclaimer: this.productPriceBreakdown.shippingDisclaimer,
      taxDisclaimer: this.productPriceBreakdown.taxDisclaimer,
    };
  }

  protected get imageMode(): ImageModeId {
    return this.styles.gallery_imageMode;
  }

  protected get imageRatio(): ImageRatioId {
    return this.styles.galleryImageRatio;
  }

  protected get htmlTags() {
    return {
      productNameHtmlTag: this.publicData.COMPONENT.gallery_productNameHtmlTag
        ? this.publicData.COMPONENT.gallery_productNameHtmlTag
        : HeadingTags.P,
      headerTextHtmlTag: this.publicData.COMPONENT.gallery_headerTextHtmlTag || HeadingTags.H2,
      categoriesFiltersHtmlTag: this.publicData.COMPONENT.gallery_categoriesFiltersHtmlTag || HeadingTags.H2,
      categoryMenuTitleHtmlTag:
        this.publicData.COMPONENT.gallery_categoryMenuTitleHtmlTag ||
        this.publicData.COMPONENT.gallery_categoriesFiltersHtmlTag ||
        HeadingTags.H2,
      filtersTitleHtmlTag:
        this.publicData.COMPONENT.gallery_filtersTitleHtmlTag ||
        this.publicData.COMPONENT.gallery_categoriesFiltersHtmlTag ||
        HeadingTags.H2,
      categoryPageHeaderHtmlTag: this.publicData.COMPONENT.gallery_categoryPageHeaderHtmlTag || HeadingTags.H1,
    };
  }

  protected get biType() {
    switch (this.type) {
      case StoresWidgetID.SLIDER_GALLERY:
        return 'minigallery';
      case StoresWidgetID.CATEGORY_PAGE:
        return 'category';
      case StoresWidgetID.SEARCH_MODAL_GALLERY:
        return 'search suggestion box';
      case StoresWidgetID.SEARCH_RESULTS_GALLERY:
        return 'search results page';
      default:
        return 'gridgallery';
    }
  }

  protected async handleOptionSelectionsChange(params: {
    productId: string;
    selectionIds: number[];
    optionType: ProductOptionType;
  }) {
    await this.productsService.handleProductsOptionsChange({
      ...params,
      productsManifest: this.productsManifest,
    });

    const {product, variant} = this.productsService.getProductAndVariantById(params.productId);

    if (this.experiments.enabled(Experiments.OptionalModifiersGallery)) {
      const canAddProductToCart = this.productsService.canAddToCart(product);
      if (canAddProductToCart) {
        this.updateAddToCartStatus(product.id, AddToCartActionStatus.IDLE);
      }
    } else {
      const isAllOptionsSelected = product.options.length === params.selectionIds.length;
      if (isAllOptionsSelected) {
        this.updateAddToCartStatus(product.id, AddToCartActionStatus.IDLE);
      }
    }

    const variantInfoMap = this.productsVariantInfoMap;

    const showHiddenVariants = this.experiments.enabled(Experiments.ShowHiddenVariants);

    const {addToCartState, isSelectedCombinationUnavailable} = resolveProductManifestState({
      addToCartService: this.addToCartService,
      product,
      variantInfo: variantInfoMap?.[product.id],
      showHiddenVariants,
      isCatalogV3: this.isCatalogV3,
      price: actualPrice(product),
      inStock: product.isInStock,
      isPreOrderState: isPreOrder(product, variant),
    });

    this.productsManifest[product.id].addToCartState = addToCartState;
    this.productsManifest[product.id].isSelectedCombinationUnavailable = isSelectedCombinationUnavailable;

    this.recomputeClientFormattedPrices(product, variant);

    this.updateComponent({
      productsManifest: this.productsManifest,
      productsVariantInfoMap: variantInfoMap,
      productsPriceRangeMap: this.productsService.productPriceRangeMap,
      products: this.productsService.products,
      clientFormattedPrices: {...this.clientFormattedPrices},
    });

    this.sendClickOnProductOptionBiEvent(params);
  }

  protected getProductsPageOffset(): number {
    return 0;
  }

  public async handleCombinedListingSiblingSwitch(
    currentProductId: string,
    siblingProductId: string,
    cardIndex: number
  ): Promise<void> {
    const productGroup = this.productGroupsMap[currentProductId];
    if (!productGroup) {
      return;
    }

    const globalIndex = this.getProductsPageOffset() + cardIndex;
    const sibling = await this.productsService.handleCombinedListingSiblingSwitch(
      currentProductId,
      siblingProductId,
      globalIndex
    );
    if (!sibling) {
      return;
    }

    this.ensureSiblingManifest(sibling);
    this.ensureSiblingProductGroup(sibling, productGroup);
    await this.preselectSiblingOptions(sibling);

    this.updateComponent({
      products: this.productsService.products,
      productsManifest: this.productsManifest,
      productsVariantInfoMap: this.productsVariantInfoMap,
      productsPriceRangeMap: this.productsService.productPriceRangeMap,
      productGroupsMap: this.productGroupsMap,
      handleCombinedListingSiblingSwitch: this.handleCombinedListingSiblingSwitch.bind(this),
    });
  }

  private ensureSiblingManifest(sibling: IProduct) {
    if (!this.productsManifest[sibling.id]) {
      this.productsManifest[sibling.id] = {
        url: `${sibling.urlPart}`,
        addToCartState: this.addToCartService.getButtonState({
          price: sibling.price,
          inStock: sibling.isInStock,
          isPreOrderState: false,
          isUnavailable:
            this.experiments.enabled(Experiments.ShowHiddenVariants) &&
            hasNoVisibleVariants({product: sibling, isCatalogV3: this.isCatalogV3}),
        }),
        productItemsState: this.productItemsService.calculateProductItemsState(sibling),
      };
    }
  }

  private ensureSiblingProductGroup(sibling: IProduct, productGroup: IProductGroup) {
    if (!this.productGroupsMap[sibling.id]) {
      const siblingColorOption = sibling.options.find((o) => o.optionType === ProductOptionType.COLOR);
      this.productGroupsMap[sibling.id] = siblingColorOption
        ? {...productGroup, customizationId: siblingColorOption.id}
        : productGroup;
    }
  }

  protected getGroupedBySelectionIds(product: IProduct): number[] {
    const groupedByOptionId = this.productGroupsMap[product.id]?.customizationId;
    const groupedByOption = product.options.find((option) => option.id === groupedByOptionId);
    const visibleSelection = groupedByOption?.selections?.find((sel) => sel.visible);
    return visibleSelection ? [visibleSelection.id] : [];
  }

  private async preselectSiblingOptions(sibling: IProduct) {
    const singleChoiceSelectionIds = this.getGroupedBySelectionIds(sibling);

    if (singleChoiceSelectionIds.length) {
      await this.productsService.handleProductsOptionsChange({
        productId: sibling.id,
        selectionIds: singleChoiceSelectionIds,
        productsManifest: this.productsManifest,
      });
    }
  }

  protected getStyleParamByDevice<T>(mobileStyleParam: T, defaultStyleParam: T, experimentEnabled = true): T {
    if (experimentEnabled && this.siteStore.isMobile() && mobileStyleParam !== undefined) {
      return mobileStyleParam;
    }
    return defaultStyleParam;
  }
  private sendClickOnProductOptionBiEvent(params: {productId: string; optionType: ProductOptionType}) {
    const {productId, optionType} = params;

    const optiontype = (
      {
        [ProductOptionType.DROP_DOWN]: BI_PRODUCT_OPTION_TYPE.LIST,
        [ProductOptionType.COLOR]: BI_PRODUCT_OPTION_TYPE.COLOR,
      } as const
    )[optionType];

    const {productType} = this.productsService.getProduct(productId);

    this.siteStore.webBiLogger.report(
      clickOnProductOptionSf({
        action: BI_PRODUCT_OPTION_ACTION.CHECKED,
        appName: BI_APP_NAME,
        optiontype,
        productId,
        productType,
        origin,
        viewMode: this.siteStore.biStorefrontViewMode,
      })
    );
  }

  protected get productsVariantInfoMap(): ProductsVariantInfoMap {
    const isClientFormatting = this.experiments.enabled(Experiments.OnlineStoresCurrencyClientFormatting);
    const options = isClientFormatting ? {isClientFormatting, flowAPI: this.flowAPI} : undefined;
    return this.shouldShowProductOptions ? this.productsService.getVariantInfoMap(options) : {};
  }

  protected get commonViewGallerySfParams() {
    const {gallery_loadMoreProductsType, gallery_showPrice, showQuickView, gallery_hoverType} = this.styles;

    const loadType = {
      [LoadMoreType.BUTTON]: BiEventParam.LoadMore,
      [LoadMoreType.PAGINATION]: BiEventParam.Pagination,
      [LoadMoreType.INFINITE]: BiEventParam.InfiniteScroll,
    }[gallery_loadMoreProductsType];

    return {
      type: this.type,
      component_id: this.compId,
      isMobileFriendly: this.siteStore.isMobileFriendly,
      addToCart: this.getStyleParamByDevice(
        this.styles['mobile:gallery_showAddToCartButton'],
        this.styles.gallery_showAddToCartButton
      ),
      navigationClick: this.biNavigationClick,
      hasPrice: gallery_showPrice,
      hasQuantity: this.getStyleParamByDevice(
        this.styles['mobile:gallery_showQuantity'],
        this.styles.gallery_showAddToCartQuantity
      ),
      priceBreakdown: this.productPriceBreakdown.priceBreakdownBIParam,
      hasQuickView: showQuickView,
      hoverType: gallery_hoverType.value,
      loadType,
      impressionId: this.impressionId,
    };
  }

  protected get commonViewGallerySfSettingsParams() {
    const {
      gallery_addToCartButtonPlacement,
      gallery_addToCartButtonContentType,
      gallery_priceAndDiscountLayout,
      gallery_namePriceLayout,
      gallery_showColorOptionsOnly,
      gallery_shouldLimitProductOptions,
      gallery_productOptionSwatchesLimit,
      gallery_quantityBoxWidth,
    } = this.styles;

    return {
      addToCartPlacement: _.startCase(AddToCartButtonPlacement[gallery_addToCartButtonPlacement]),
      priceAndDiscountLayout: _.startCase(_.camelCase(PriceAndDiscountLayouts[gallery_priceAndDiscountLayout])),
      nameAndPriceLayout: _.startCase(NamePriceLayout[gallery_namePriceLayout]),
      showColorOptionsOnly: gallery_showColorOptionsOnly,
      shouldLimitProductOptions: gallery_shouldLimitProductOptions,
      productOptionSwatchesLimit: gallery_productOptionSwatchesLimit,
      addToCartButtonContentType: _.startCase(ButtonContentType[gallery_addToCartButtonContentType]),
      quantityBoxWidth: gallery_quantityBoxWidth,
    };
  }

  private get biNavigationClick(): string {
    const {gallery_showAddToCartButton, gallery_addToCartAction} = this.styles;
    if (!gallery_showAddToCartButton) {
      return '';
    } else if (gallery_addToCartAction === AddToCartActionOption.MINI_CART) {
      return 'mini-cart';
    } else if (gallery_addToCartAction === AddToCartActionOption.CART) {
      return 'cart';
    }
    return 'none';
  }

  private get isShowProductOptionsInAnyBreakpoint(): boolean {
    const styleParam = this.siteStore.isMobile()
      ? this.stylesParams['mobile:gallery_showProductOptions']
      : this.stylesParams.gallery_showProductOptionsButton;

    return this.isTrueInAnyBreakpoint((bpStyle) => bpStyle[styleParam.key] === true);
  }

  private get isAddToCartFlowTypeIsQuickAddInAnyBreakpoint(): boolean {
    const addToCartFlowTypeStyleParamKey = this.stylesParams.gallery_addToCartFlowType.key;
    return this.isTrueInAnyBreakpoint(
      (bpStyle) => bpStyle[addToCartFlowTypeStyleParamKey] === AddToCartFlowType.QUICK_ADD
    );
  }

  protected get shouldShowProductOptions(): boolean {
    return this.isShowProductOptionsInAnyBreakpoint || this.isAddToCartFlowTypeIsQuickAddInAnyBreakpoint;
  }

  protected async setProductItemSlotsProps(products: IProduct[]) {
    const gallerySlotIds = Object.values(ProductItemSlotIds);

    /* istanbul ignore next: yoshi missing a testkit for repeated slots  */
    if (this.experiments.enabled(Experiments.RepeatedSlots)) {
      try {
        for (const product of products) {
          const slot = await this.slotAPIFactory.createRepeatedSlotAPI(
            ProductItemSlotIds.GalleryItemAdditionalButtons,
            product.id
          );
          slot.productId = product.id;
          slot.sku = product.sku;
        }
      } catch (e) {
        this.reportError(e);
      }
    }

    for (const slotId of gallerySlotIds) {
      try {
        const slot = this.slotAPIFactory.getSlotAPI(slotId);
        slot.namespace = STORES_NAMESPACE;

        slot.resourceList = products.map((product) => {
          const maybeAdditionalButtonsSlotFields =
            slotId === ProductItemSlotIds.GalleryItemAdditionalButtons
              ? {
                  sku: product.sku,
                  isPreOrder: !!isPreOrder(product),
                }
              : {};

          return {
            id: product.id,
            ...maybeAdditionalButtonsSlotFields,
          };
        });
      } catch (e) {
        /* istanbul ignore next: nothing to test  */
        this.reportError(e);
      }
    }
  }

  protected get isUsingAlternativeCurrency() {
    return this.viewerContext.currency !== this.viewerContext.getCurrentCurrency();
  }

  protected readonly updateAddToCartStatus = (productId: string, status: AddToCartActionStatus, slotIndex?: number) => {
    this.addedToCartStatus[productId] = status;
    const unselectedOptionErrorSlotIndex = status === AddToCartActionStatus.UNSELECTED_OPTION_ERROR ? slotIndex : null;
    const addToCartInProgressSlotIndex = status === AddToCartActionStatus.IN_PROGRESS ? slotIndex : null;
    this.updateComponent({
      addedToCartStatus: this.addedToCartStatus,
      unselectedOptionErrorSlotIndex,
      addToCartInProgressSlotIndex,
    });
  };

  protected getAddToCartAction(): AddToCartActionOption {
    return this.styles.gallery_addToCartAction;
  }

  protected async handleQuickAddToCart({
    product,
    productId,
    quantity,
    slotIndex,
  }: {
    product: IProduct;
    productId: string;
    quantity: number;
    slotIndex?: number;
  }) {
    this.updateAddToCartStatus(productId, AddToCartActionStatus.IN_PROGRESS, slotIndex);

    this.productsService.closeQuickAdd(this.productsManifest, productId);

    await this.productsService.handleDirectAddToCart({
      product,
      productId,
      quantity,
      addToCartAction: this.getAddToCartAction(),
    });

    this.productsService.clearSelectionsForProduct(productId);
    this.updateComponent({productsVariantInfoMap: this.productsVariantInfoMap});

    this.updateAddToCartStatus(productId, AddToCartActionStatus.SUCCESSFUL);
  }

  protected get isSliderGallery(): boolean {
    return this.type === StoresWidgetID.SLIDER_GALLERY;
  }

  protected get storesWidgetId(): string {
    return this.type;
  }

  private findProductWithMostOptions(products: IProduct[]) {
    return products.reduce((maxProduct, currentProduct) => {
      const currentOptionsCount = currentProduct.options.length;
      const maxOptionsCount = maxProduct.options.length;
      return currentOptionsCount > maxOptionsCount ? currentProduct : maxProduct;
    }, products[0]);
  }

  private shouldShowQuickAddInEditorForProduct(products: IProduct[], productId: IProduct['id']) {
    if (this.viewState !== GalleryWidgetViewState.QUICK_ADD) {
      return false;
    }
    if (!this.siteStore.isMobile()) {
      return true;
    }
    const productWithMostOptions = this.findProductWithMostOptions(products);
    return productId === productWithMostOptions?.id;
  }

  private handleEditorModeQuickAdd() {
    const products = this.productsService.products;
    const hasProductsWithOptions = products.some((product) => product.options.length > 0);
    if (!hasProductsWithOptions) {
      return;
    }

    const updatedProductsManifest = {...this.productsManifest};
    products.forEach((product) => {
      const shouldSetQuickAddState = this.shouldShowQuickAddInEditorForProduct(products, product.id);
      const baseState = this.productItemsService.calculateProductItemsState(product);
      updatedProductsManifest[product.id].productItemsState = shouldSetQuickAddState
        ? ProductItemsState.QUICK_ADD
        : baseState;
    });
    this.updateComponent({productsManifest: updatedProductsManifest});
  }

  public updateWidgetViewState(viewState: GalleryWidgetViewState) {
    if (this.viewState === viewState) {
      return;
    }
    this.viewState = viewState;

    /* istanbul ignore else */
    if (viewState === GalleryWidgetViewState.QUICK_ADD || viewState === GalleryWidgetViewState.DEFAULT) {
      this.handleEditorModeQuickAdd();
    }
  }

  protected createWishlistBiEventData(productId: string) {
    const {options, productType} = this.productsService.getProduct(productId);
    return {
      appName: BI_APP_NAME,
      hasOptions: options.length > 0,
      productId,
      productType,
      origin,
    };
  }

  private get isWishlistEnabledInAnyBreakpoint(): boolean {
    return this.isTrueInAnyBreakpoint((bpStyle) => bpStyle.gallery_wishlistEnabled);
  }
  protected async loadUserWishlistProductIds(): Promise<void> {
    if (!this.experiments.enabled(Experiments.ShowWishlistInGallery)) {
      return;
    }
    if (this.siteStore.isSSR()) {
      return;
    }
    if (!this.isWishlistEnabledInAnyBreakpoint) {
      return;
    }
    if (!this.siteStore.usersApi.currentUser.loggedIn) {
      return;
    }

    await this.wishlistService.fetchUserWishlistMap();
  }
}
